// The phone book (Stage 4.8f.4a, mockup "Phone book"): the team as "Team visibility" shows it (teamScope.js) with how each is now, the
// departments and who is free in them, the customers the CRM lets this person see, and their own favourites (a star on any of them).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, InputBase, Typography, CircularProgress } from "@mui/material";
import { Search, Phone, Star, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { transferTargets, searchCustomers, favourites as loadFavourites, addFavourite, removeFavourite } from "../pages/agent/phoneApi";
import { Initial, WithDot, RowCall, Segments, SectionTitle } from "./phoneUi";

const phoneOf = (c) => c?.phoneE164 || c?.callerId || "";
const fullName = (c) => [c?.firstName, c?.lastName].filter(Boolean).join(" ").trim();

export default function PhoneBook({ ready, onCall }) {
  const { t } = useTranslation("phone");
  const [tab, setTab] = useState("team");
  const [q, setQ] = useState("");
  const [team, setTeam] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [busyCustomers, setBusyCustomers] = useState(false);
  const [favs, setFavs] = useState([]);

  const loadTeam = useCallback(() => transferTargets().then(setTeam).catch(() => setTeam((x) => x ?? { people: [], departments: [] })), []);
  useEffect(() => { loadTeam(); const id = setInterval(loadTeam, 10000); return () => clearInterval(id); }, [loadTeam]);
  useEffect(() => { loadFavourites().then((r) => setFavs(r.favourites ?? [])).catch(() => {}); }, []);

  // Customers: searched as typed (the first page of the CRM's own search, as permissions allow).
  useEffect(() => {
    if (tab !== "customers") return undefined;
    const id = setTimeout(() => {
      setBusyCustomers(true);
      searchCustomers(q.trim()).then((r) => setCustomers(r.contacts ?? r.items ?? [])).catch(() => setCustomers([])).finally(() => setBusyCustomers(false));
    }, 350);
    return () => clearTimeout(id);
  }, [q, tab]);

  const isFav = (kind, ref) => favs.find((f) => f.kind === kind && f.ref === String(ref));
  const toggleFav = async (kind, ref, label) => {
    const f = isFav(kind, ref);
    try {
      const r = f ? await removeFavourite(f.id) : await addFavourite({ kind, ref: String(ref), label });
      setFavs(r.favourites ?? []);
    } catch { /* stays as it was */ }
  };

  const needle = q.trim().toLowerCase();
  const people = useMemo(() => (team?.people ?? []).filter((p) => !needle || `${p.name} ${p.ext} ${p.section ?? ""}`.toLowerCase().includes(needle)), [team, needle]);
  const depts = useMemo(() => (team?.departments ?? []).filter((d) => !needle || d.name.toLowerCase().includes(needle)), [team, needle]);
  const statusWord = (s) => t(`status.${s}`, { defaultValue: t("status.unknown") });

  const Star_ = ({ kind, refId, label }) => {
    const on = !!isFav(kind, refId);
    return (
      <ButtonBase onClick={() => toggleFav(kind, refId, label)} aria-pressed={on} aria-label={t(on ? "favRemove" : "favAdd", { name: label })} title={t(on ? "favRemove" : "favAdd", { name: label })}
        sx={{ width: 36, height: 36, borderRadius: "8px", color: on ? "var(--warning)" : "var(--ink-500)" }}>
        <Star size={17} strokeWidth={1.8} fill={on ? "currentColor" : "none"} aria-hidden="true" />
      </ButtonBase>
    );
  };

  const personRow = (p) => (
    <Box component="li" key={`p-${p.ext}`} sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px" }}>
      <WithDot status={p.status}><Initial name={p.name} /></WithDot>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography dir="auto" noWrap sx={{ font: "700 14px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)", color: "var(--ink-900)" }}>{p.name}</Typography>
        <Typography noWrap sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{[statusWord(p.status), p.owner ? t("ownerLabel") : p.section, t("extShort", { ext: p.ext })].filter(Boolean).join(" · ")}</Typography>
      </Box>
      <Star_ kind="person" refId={p.ext} label={p.name} />
      <RowCall icon={Phone} label={t("callName", { name: p.name })} disabled={!ready || p.status === "dnd"} onClick={() => onCall(p.ext, p.name)} />
    </Box>
  );
  const customerRow = (c) => {
    const name = fullName(c) || phoneOf(c);
    const num = phoneOf(c);
    return (
      <Box component="li" key={`c-${c.id}`} sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px" }}>
        <Initial name={name} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography dir="auto" noWrap sx={{ font: "700 14px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)", color: "var(--ink-900)" }}>{name}</Typography>
          <Typography noWrap sx={{ font: "500 12px/1.3 var(--font-ui)", color: c.doNotContact ? "var(--danger)" : "var(--ink-500)" }}>
            <span dir="ltr">{num}</span>{c.doNotContact ? ` · ${t("doNotContact")}` : ""}
          </Typography>
        </Box>
        <Star_ kind="customer" refId={num} label={name} />
        <RowCall icon={Phone} label={t("callName", { name })} disabled={!ready || !num || !!c.doNotContact} onClick={() => onCall(num, name)} />
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Box sx={{ p: "14px 16px 8px", display: "flex", flexDirection: "column", gap: "10px" }}>
        <Box component="label" sx={{ display: "flex", alignItems: "center", gap: "8px", height: 42, px: "12px", border: "1px solid var(--line-200)", borderRadius: "12px", background: "var(--surface-2)" }}>
          <Search size={18} strokeWidth={1.8} aria-hidden="true" style={{ color: "var(--ink-500)" }} />
          <InputBase value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("searchBook")} inputProps={{ "aria-label": t("searchBook") }}
            sx={{ flex: 1, minWidth: 0, font: "500 14px/1 var(--font-ui)", color: "var(--ink-900)" }} />
        </Box>
        <Segments dark label={t("who")} value={tab} onChange={setTab}
          items={[{ value: "team", label: t("team") }, { value: "customers", label: t("customers") }, { value: "favourites", label: t("favourites") }]} />
      </Box>

      <Box sx={{ overflowY: "auto", flex: 1, pb: 1 }}>
        {tab === "team" && (!team ? <Box sx={{ p: 4, display: "grid", placeItems: "center" }}><CircularProgress size={20} sx={{ color: "var(--brand-violet)" }} /></Box> : (
          <>
            {depts.length > 0 && <SectionTitle>{t("departments")}</SectionTitle>}
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: "0 8px" }}>
              {depts.map((d) => (
                <Box component="li" key={`d-${d.name}`} sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px" }}>
                  <Box aria-hidden="true" sx={{ width: 36, height: 36, borderRadius: "12px", display: "grid", placeItems: "center", background: "var(--c-cyan-bg)", color: "var(--c-cyan)" }}><Users size={18} strokeWidth={1.8} /></Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ font: "700 14px/1.2 var(--font-ui)", color: "var(--ink-900)" }}>{d.name}</Typography>
                    <Typography noWrap sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{d.available ? t("deptAvailable", { count: d.available }) : t("deptNobody")}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <SectionTitle>{t("people")}</SectionTitle>
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: "0 8px" }}>{people.map(personRow)}</Box>
            {!people.length && <Typography sx={{ p: "8px 16px", font: "500 13px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t("noPeople")}</Typography>}
          </>
        ))}
        {tab === "customers" && (
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: "0 8px" }}>
            {busyCustomers && !customers.length ? <Box sx={{ p: 4, display: "grid", placeItems: "center" }}><CircularProgress size={20} sx={{ color: "var(--brand-violet)" }} /></Box> : customers.map(customerRow)}
            {!busyCustomers && !customers.length && <Typography sx={{ p: "8px 8px", font: "500 13px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t("noCustomers")}</Typography>}
          </Box>
        )}
        {tab === "favourites" && (
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: "0 8px" }}>
            {favs.filter((f) => !needle || `${f.label ?? ""} ${f.ref}`.toLowerCase().includes(needle)).map((f) => {
              if (f.kind === "person") {
                const p = (team?.people ?? []).find((x) => x.ext === f.ref);
                return personRow(p ?? { ext: f.ref, name: f.label || f.ref, status: "unknown", section: null });
              }
              return customerRow({ id: `${f.kind}-${f.ref}`, firstName: f.label || f.ref, phoneE164: f.ref });
            })}
            {!favs.length && <Typography sx={{ p: "8px 8px", font: "500 13px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t("noFavourites")}</Typography>}
          </Box>
        )}
      </Box>
      <Typography sx={{ p: "10px 16px", borderTop: "1px solid var(--line-100)", font: "500 12px/1.4 var(--font-ui)", color: "var(--ink-500)" }}>{t("bookFoot")}</Typography>
    </Box>
  );
}
